import { useStore } from '@mirai/data-sources';
import { useEffect, useRef } from 'react';

import { getSafeTrust } from './safe';

const leases = new WeakMap();

const TrustManager = () => {
  const { value: store = {} } = useStore();
  const engine = getSafeTrust(store);
  const { skeleton = false } = store;
  const context = useRef(store);
  context.current = store;

  useEffect(() => {
    if (skeleton) return;
    const lease = leases.get(engine);
    if (lease) {
      lease.contexts.add(context);
      return () => {
        lease.contexts.delete(context);
        if (!lease.contexts.size) lease.cleanup();
      };
    }

    engine.start();
    const contexts = new Set([context]);
    const evaluate = () => engine.evaluate([...contexts].at(-1).current);
    const fields = new WeakMap();
    let fieldCount = 0;
    const sampledAt = {};
    const handleInteraction = (event) => {
      const { target } = event;
      const zone = target?.closest?.('[data-trust-stage]');
      if (
        zone?.getAttribute('data-trust-scope') === engine.scope &&
        ['click', 'input', 'change'].includes(event.type) &&
        !target.closest('[data-trust-exclude]') &&
        target.type !== 'password'
      ) {
        const stage = zone.getAttribute('data-trust-stage');
        engine.record('journeyAction', { stage, name: `${stage}Activity`, trusted: event.isTrusted });
      }
      const form = target?.closest?.('[data-trust-fields="true"], [data-trust-action="checkout"]');
      if (!form || form.getAttribute('data-trust-scope') !== engine.scope) return;
      if (target.closest('[data-trust-exclude]') || target.type === 'password') return;
      if (event.type === 'keydown' && event.repeat) return;
      if (['pointermove', 'scroll'].includes(event.type)) {
        const at = performance.now();
        if (sampledAt[event.type] !== undefined && at - sampledAt[event.type] < 250) return;
        sampledAt[event.type] = at;
      }
      if (target.matches?.('input, select, textarea') && !fields.has(target) && fieldCount < 100)
        fields.set(target, fieldCount++);
      const types = {
        input: 'edits',
        paste: 'pastes',
        compositionend: 'composition',
        focusin: 'focus',
        pointermove: 'pointerMoves',
        scroll: 'scrolls',
      };
      engine.record('interaction', {
        event: event.type,
        field: fields.get(target),
        activation: event.type === 'click' && form.getAttribute('data-trust-action') === 'checkout',
        type: event.isTrusted ? types[event.type] || 'inputs' : 'synthetic',
        modality: event.pointerType || (event.type === 'keydown' ? 'keyboard' : undefined),
        correction: event.inputType === 'deleteContentBackward' || event.inputType === 'deleteContentForward',
        navigationKey: event.type === 'keydown' && ['Tab', 'Enter'].includes(event.key),
      });
    };
    const handleVisibility = () => engine.visibility(document.hidden);
    const handlePageHide = () => engine.visibility(true);
    const handlePageShow = () => {
      handleVisibility();
      evaluate();
    };
    const handleStorage = ({ key }) => {
      if (key !== null && key !== engine.storageKey) return;
      evaluate();
    };
    const events = [
      'input',
      'paste',
      'compositionend',
      'focusin',
      'pointerdown',
      'keydown',
      'click',
      'pointermove',
      'scroll',
      'change',
    ];
    events.forEach((event) => document.addEventListener(event, handleInteraction, true));
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    const timer = window.setInterval(evaluate, 10000);
    const current = {
      contexts,
      cleanup: () => {
        events.forEach((event) => document.removeEventListener(event, handleInteraction, true));
        document.removeEventListener('visibilitychange', handleVisibility);
        window.removeEventListener('storage', handleStorage);
        window.removeEventListener('pagehide', handlePageHide);
        window.removeEventListener('pageshow', handlePageShow);
        window.clearInterval(timer);
        engine.stop();
        leases.delete(engine);
      },
    };
    leases.set(engine, current);
    return () => {
      contexts.delete(context);
      if (!contexts.size) current.cleanup();
    };
  }, [engine, skeleton]);

  return null;
};

export { TrustManager };
